import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Reveal, SectionHeader } from "@/components/ui-blocks/Reveal";
import { CountUp } from "@/components/ui-blocks/CountUp";
import { PieChart, Users, FileWarning, Sparkles } from "lucide-react";

const COLORS = ["hsl(var(--orange))", "hsl(var(--green))", "hsl(var(--saffron))", "hsl(var(--india-green))", "hsl(var(--foreground))", "hsl(var(--muted-foreground))"];

export const TransparencyDashboard = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/transparency-summary").then(setData).catch(() => {}); }, []);
  const spend = data?.spend_breakdown || { "Programs & Beneficiaries": 93, "Governance & Compliance": 4, "Fundraising & Communications": 3 };
  const byPurpose = data ? Object.entries(data.by_purpose || {}) : [];
  const totalPurpose = byPurpose.reduce((s, [, v]) => s + Number(v), 0) || 1;

  return (
    <section className="bg-background">
      <div className="container-wide py-14 lg:py-20">
        <SectionHeader eyebrow="Transparency Dashboard" title="Live donation intent, spend split, and program reach." lead="Every rupee tagged. Every outcome tracked. Every year audited." />
        <div className="grid gap-5 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="glass rounded-2xl p-6 caf-shadow h-full">
              <div className="eyebrow">This year</div>
              <div className="mt-2 text-3xl font-extrabold text-foreground tabular"><CountUp value={data?.total_donation_intent_inr || 0} /> <span className="text-lg text-[hsl(var(--muted-ink))]">INR</span></div>
              <div className="text-xs text-[hsl(var(--muted-ink))] mt-1">Total donation intent recorded (v1 placeholder)</div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <MiniStat Icon={Users} label="Volunteers" value={data?.volunteers_count || 0} />
                <MiniStat Icon={FileWarning} label="Reports" value={data?.reports_count || 0} />
                <MiniStat Icon={Sparkles} label="Intents" value={data?.donation_intents_count || 0} />
                <MiniStat Icon={PieChart} label="Programs" value={12} />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.05} className="lg:col-span-4">
            <div className="rounded-2xl border border-border bg-background p-6 h-full">
              <div className="eyebrow">Planned spend split (FY 2026-27)</div>
              <div className="mt-4 space-y-3">
                {Object.entries(spend).map(([k, v], i) => (
                  <div key={k}>
                    <div className="flex items-center justify-between text-sm"><span className="text-foreground font-semibold">{k}</span><span className="text-[hsl(var(--muted-ink))]">{v}%</span></div>
                    <div className="mt-1 h-2 rounded-full bg-[hsl(var(--surface-tint))] overflow-hidden"><div className="h-full" style={{ width: `${v}%`, background: COLORS[i % COLORS.length] }} /></div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4">
            <div className="rounded-2xl border border-border bg-background p-6 h-full">
              <div className="eyebrow">By program (live intent)</div>
              {byPurpose.length === 0 ? (
                <div className="mt-6 text-sm text-[hsl(var(--muted-ink))]">No donation intents yet. As soon as intents are recorded, they’ll appear here — live.</div>
              ) : (
                <div className="mt-4 space-y-3">
                  {byPurpose.map(([k, v], i) => {
                    const pct = Math.round((Number(v) / totalPurpose) * 100);
                    return (
                      <div key={k}>
                        <div className="flex items-center justify-between text-sm"><span className="text-foreground font-semibold">{k}</span><span className="text-[hsl(var(--muted-ink))]">₹{Number(v).toLocaleString("en-IN")} ({pct}%)</span></div>
                        <div className="mt-1 h-2 rounded-full bg-[hsl(var(--surface-tint))] overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} /></div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const MiniStat = ({ Icon, label, value }) => (
  <div className="rounded-lg border border-border bg-background p-3">
    <div className="flex items-center gap-2 text-[hsl(var(--muted-ink))] text-xs"><Icon className="h-3.5 w-3.5" /> {label}</div>
    <div className="mt-1 text-lg font-bold text-foreground tabular"><CountUp value={Number(value) || 0} /></div>
  </div>
);
